import { TextTooltip } from '../text-tooltip';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { InteractSettingsIcon, ViewErrorIcon, ViewHitIcon, ViewWarnIcon } from '@/client/assets';
import { Popover, PopoverContent, PopoverTrigger } from '@/client/components/ui/popover';
import { OPEN_SLIPPAGE } from '@/client/confs/app';
import { useTradeSettingStore } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { handleKeyDownDisableNumber } from '@/client/utils/handle';
import { deadlineSchema, slippageSchema } from '@/client/utils/schema';
import { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const TradeSettings = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const deadline = useTradeSettingStore(state => state.deadline);
  const slippage = useTradeSettingStore(state => state.slippage);
  const slippageAuto = useTradeSettingStore(state => state.slippageAuto);
  const setDeadline = useTradeSettingStore(state => state.setDeadline);
  const setSlippage = useTradeSettingStore(state => state.setSlippage);
  const setSlippageAuto = useTradeSettingStore(state => state.setSlippageAuto);
  // Empty input = Auto (price-impact buffer). A number = manual max-slippage %.
  const [inputSlippage, setInputSlippage] = useState<string>(slippageAuto ? '' : String(slippage));
  const [inputDeadline, setInputDeadline] = useState<string>(deadline.toString());
  const numSlippage = useMemo(() => Number(inputSlippage || slippage), [inputSlippage, slippage]);
  // Warnings only meaningful for a manual value; Auto is never "high slippage".
  const isHighSlippage = useMemo(() => !slippageAuto && numSlippage > 5 && numSlippage < 50, [slippageAuto, numSlippage]);
  const isVeryHighSlippage = useMemo(() => !slippageAuto && numSlippage >= 50, [slippageAuto, numSlippage]);
  const numDeadline = useMemo(() => Number(inputDeadline || deadline), [deadline, inputDeadline]);
  const maxDeadline = 1 * 24 * 60;
  const isMaxDeadline = useMemo(() => numDeadline >= 1 * 24 * 60, [numDeadline]);

  const handleSlippage = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const { success } = slippageSchema.safeParse(Number(value));
    if (success || value == '') {
      const decimalParts = value.split('.');
      if (decimalParts.length > 1 && decimalParts[1].length > 2) return;
      setInputSlippage(value);
      // Empty → back to Auto; any number → manual mode with that value.
      if (value === '') {
        setSlippageAuto(true);
      } else {
        setSlippageAuto(false);
        setSlippage(Number(value));
      }
    }
  };

  const handleDeadline = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace('.', '');
    const { success } = deadlineSchema.safeParse(Number(value));
    if (success || value == '') {
      setInputDeadline(value);
      setDeadline(Number(value));
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          'flex items-center gap-1',
          isHighSlippage && 'text-[#F8712B]',
          isVeryHighSlippage && 'text-[#F95E5E]'
        )}
      >
        {OPEN_SLIPPAGE && (
          <>
            {isHighSlippage && <ViewWarnIcon className="size-3 fill-current" />}
            {isVeryHighSlippage && <ViewErrorIcon className="size-3 fill-current" />}
            <span className="text-xs leading-none font-medium">{slippageAuto ? t('auto') : `${slippage}%`}</span>
          </>
        )}
        <InteractSettingsIcon className="size-4 fill-foreground/70 duration-200 hover:rotate-90" />
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={e => e.preventDefault()}
        align="end"
        className="space-y-6 w-full max-w-47.5 mt-2 p-5 -translate-y-2 translate-x-2 !rounded-4 bg-card border border-foreground/10"
      >
        {OPEN_SLIPPAGE && (
          <div className="flex space-y-3 flex-col pos-relative">
            <span className="text-sm font-medium leading-5 text-foreground/70 inline-flex items-center gap-1">
              <span>{t('max-slippage')}</span>
              <TextTooltip show={open} tooltip={t('to-manage-risk-your-transaction-will-be-canceled-i')}>
                <ViewHitIcon className="size-3.5 fill-current cursor-pointer" />
              </TextTooltip>
            </span>
            <Input
              value={inputSlippage}
              onKeyDown={handleKeyDownDisableNumber}
              onChange={handleSlippage}
              className={cn(
                'text-base font-semibold h-8.5 py-1 leading-3.5 rounded-full text-end pe-6 !ring-none',
                !isHighSlippage && !isVeryHighSlippage && 'focus:border-beneficial',
                isHighSlippage && '!text-[#F8712B] border-[#F8712B]',
                isVeryHighSlippage && '!text-destructive border-destructive'
              )}
            />
            {isHighSlippage && (
              <div className="text-[#F8712B] text-3 leading-3 font-medium flex items-center gap-1">
                <ViewWarnIcon className="size-3.5 fill-current" />
                {t('high-slippage')}
              </div>
            )}
            {isVeryHighSlippage && (
              <div className="text-destructive text-3 leading-3 font-medium flex items-center gap-1">
                <ViewErrorIcon className="size-3.5 fill-current" />
                {t('very-high-slippage')}
              </div>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setInputSlippage('');
                setSlippageAuto(true);
              }}
              className={cn(
                'pos-absolute start-1 shadow-none top-5.75 h-7 px-4 rounded-full text-sm  leading-3.5 font-semibold',
                slippageAuto ? 'bg-beneficial/20 text-beneficial-foreground' : 'line-through text-foreground/40'
              )}
            >
              {t('auto')}
            </Button>
            <div className="pos-absolute end-2 top-6.7">
              <span className="text-sm  font-medium leading-5 text-foreground/40">%</span>
            </div>
          </div>
        )}
        <div className="flex space-y-3 flex-col pos-relative">
          <span className="text-sm font-medium leading-5 text-foreground/70 inline-flex items-center gap-1">
            <span>{t('trade-deadline')}</span>
            <TextTooltip
              show={open}
              tooltip={t('your-transaction-will-revert-if-it-is-pending-for-', { day: maxDeadline / 60 / 24 })}
            >
              <ViewHitIcon className="size-3.5 fill-current cursor-pointer" />
            </TextTooltip>
          </span>
          <Input
            value={inputDeadline}
            onKeyDown={handleKeyDownDisableNumber}
            onChange={handleDeadline}
            className={cn(
              'px-4 h-8.5 py-1 font-semibold leading-3.5 rounded-full pe-15.5 !ring-none',
              !isMaxDeadline && 'focus:border-beneficial',
              isMaxDeadline && '!text-destructive border-destructive'
            )}
          />
          {isMaxDeadline && (
            <div className="text-destructive text-3 leading-3 font-medium flex items-center gap-1">
              <ViewErrorIcon className="size-3.5 fill-current" />
              {t('maximum-maxdeadline', { maxDeadline, day: maxDeadline / 60 / 24 })}
            </div>
          )}
          <div className="pos-absolute end-2 top-6.5">
            <span className="text-sm  font-medium leading-5 text-foreground/40">{t('minutes')}</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
